import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../context/StoreContext";
import { useLang } from "../context/LanguageContext";
import { fmtPrice } from "./Reveal";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, cartTotal } = useStore();
  const { t } = useLang();
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
            className="fixed inset-0 bg-inverse/60 backdrop-blur-sm z-[60]" data-testid="cart-overlay" />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 h-full w-full sm:w-[440px] bg-[color:var(--bg)] z-[70] flex flex-col border-l border-ink/10"
            data-testid="cart-drawer">
            <div className="flex items-center justify-between px-6 py-5 border-b border-ink/10">
              <h3 className="font-display font-extrabold text-2xl">{t("cart.title")}</h3>
              <button onClick={() => setCartOpen(false)} data-testid="close-cart-btn" className="p-2 hover:text-gold transition-colors"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {cart.length === 0 ? (
                <p className="text-ink/50 mt-10 text-center font-mono text-sm">{t("cart.empty")}</p>
              ) : cart.map(({ product, quantity }) => (
                <div key={product.id} className="flex gap-4 py-5 border-b border-ink/10" data-testid={`cart-item-${product.id}`}>
                  <div className="w-20 h-20 bg-surface overflow-hidden shrink-0">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-medium leading-tight">{product.name}</p>
                    <p className="text-sm text-ink/50 mb-2">{fmtPrice(product.price, product.currency)}</p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center border border-ink/15">
                        <button onClick={() => updateQty(product.id, quantity - 1)} className="p-1.5 hover:text-gold" data-testid={`dec-${product.id}`}><Minus className="w-3.5 h-3.5" /></button>
                        <span className="px-3 text-sm font-mono">{quantity}</span>
                        <button onClick={() => updateQty(product.id, quantity + 1)} className="p-1.5 hover:text-gold" data-testid={`inc-${product.id}`}><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <button onClick={() => removeFromCart(product.id)} className="text-ink/40 hover:text-red-600 transition-colors" data-testid={`remove-${product.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {cart.length > 0 && (
              <div className="px-6 py-5 border-t border-ink/10">
                <div className="flex justify-between items-baseline mb-5">
                  <span className="overline text-ink/50">{t("cart.total")}</span>
                  <span className="font-display font-extrabold text-3xl" data-testid="cart-total">{fmtPrice(cartTotal)}</span>
                </div>
                <button data-testid="go-checkout-btn"
                  onClick={() => { setCartOpen(false); navigate("/carrito"); }}
                  className="w-full bg-blue text-[color:var(--bg)] py-4 flex items-center justify-center gap-2 group hover:bg-gold hover:text-inverse transition-colors duration-300 font-medium">
                  {t("cart.checkout")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
